import type { BaseQueryFn, EndpointBuilder } from '@reduxjs/toolkit/query'
import { createApi } from '@reduxjs/toolkit/query/react'

import { HTTP_METHODS } from 'common/constants/httpMethodsConstants'
import { baseQuery } from 'common/store/api/fetchBaseQuery'
import { REDUCERS } from 'common/store/reducerConstants'

import { siteHeaders } from './emrHeaders'
import {
  transformAppointmentIssuesResponse,
  transformMedicalIssueResponse,
  transformMedicalIssuesResponse,
  transformProcedureSearchResponse,
} from './medicalIssues.transform'
import type {
  AppointmentIssuesSet,
  CreatePatientIssueBody,
  MedicalIssue,
  ProcedureSearchResult,
  ReplaceAppointmentIssuesBody,
} from './medicalIssues.types'

export enum MEDICAL_ISSUES_TAGS {
  PATIENT_ISSUES = 'PatientMedicalIssues',
  APPOINTMENT_ISSUES = 'AppointmentMedicalIssues',
}

export const medicalIssuesApi = createApi({
  reducerPath: REDUCERS.MEDICAL_ISSUES,
  baseQuery: baseQuery(),
  tagTypes: Object.values(MEDICAL_ISSUES_TAGS),
  endpoints: (build: EndpointBuilder<BaseQueryFn, string, string>) => ({
    fetchPatientMedicalIssues: build.query<MedicalIssue[], { patientId: number }>({
      query: ({ patientId }) => ({
        url: `/emr/patient/${patientId}/medical-issues`,
      }),
      transformResponse: transformMedicalIssuesResponse,
      providesTags: [MEDICAL_ISSUES_TAGS.PATIENT_ISSUES],
    }),

    createPatientMedicalIssue: build.mutation<
      MedicalIssue | null,
      { patientId: number; data: CreatePatientIssueBody }
    >({
      query: ({ patientId, data }) => ({
        url: `/emr/patient/${patientId}/medical-issues`,
        method: HTTP_METHODS.POST,
        data,
      }),
      transformResponse: transformMedicalIssueResponse,
      invalidatesTags: [MEDICAL_ISSUES_TAGS.PATIENT_ISSUES],
    }),

    fetchAppointmentMedicalIssues: build.query<
      AppointmentIssuesSet,
      { appointmentId: number; siteId: string }
    >({
      query: ({ appointmentId, siteId }) => ({
        url: `/emr/appointment/${appointmentId}/medical-issues`,
        headers: siteHeaders(siteId),
      }),
      transformResponse: transformAppointmentIssuesResponse,
      providesTags: [MEDICAL_ISSUES_TAGS.APPOINTMENT_ISSUES],
    }),

    replaceAppointmentMedicalIssues: build.mutation<
      AppointmentIssuesSet,
      { appointmentId: number; siteId: string; data: ReplaceAppointmentIssuesBody }
    >({
      query: ({ appointmentId, siteId, data }) => ({
        url: `/emr/appointment/${appointmentId}/medical-issues`,
        method: HTTP_METHODS.PUT,
        headers: siteHeaders(siteId),
        data,
      }),
      transformResponse: transformAppointmentIssuesResponse,
      // A picked procedure also becomes a patient problem, so both caches go stale.
      invalidatesTags: [MEDICAL_ISSUES_TAGS.APPOINTMENT_ISSUES, MEDICAL_ISSUES_TAGS.PATIENT_ISSUES],
    }),

    searchProcedures: build.query<ProcedureSearchResult, { q: string; siteId: string }>({
      query: ({ q, siteId }) => ({
        url: '/emr/procedures/search',
        params: { q },
        headers: siteHeaders(siteId),
      }),
      transformResponse: transformProcedureSearchResponse,
    }),
  }),
})

export const {
  useFetchPatientMedicalIssuesQuery,
  useCreatePatientMedicalIssueMutation,
  useFetchAppointmentMedicalIssuesQuery,
  useReplaceAppointmentMedicalIssuesMutation,
  useLazySearchProceduresQuery,
} = medicalIssuesApi
